import {Box, Button, DropdownItem} from '@viptech/react-components'
import sub from 'date-fns/sub'
import moment from 'moment'
import {useSnackbar} from 'notistack'
import {useEffect, useRef, useState} from 'react'
import WebSocketStatus from 'src/common/components/websocket-status/WebsocketStatus'
import {AdvancedFilters, FilterSelectedFinal} from 'src/components/advancedFilters/AdvancedFilters'
import {TransparentButton} from 'src/components/advancedFilters/AdvancedFiltersBase'
import {MultipleSearchDropdownItem, SelectWithSearchComponent} from 'src/components/selectWithSearchComponent/SelectWithSearchComponent'
import TopBar from 'src/components/top-bar/TopBar'
import useCameras from 'src/hooks/api/camera/useCameras'
import useClients from 'src/hooks/api/client/useClients'
import useLicensePlate from 'src/hooks/api/lpr/useLicensePlate'
import {WebSocketStatusesEnum} from 'src/hooks/api/socket/useWebsocket'
import DeleteIcon from 'src/images/DeleteIcon'
import DownloadIcon from 'src/images/DownloadIcon'
import {useCompaniesSelector} from 'src/layouts/main-layout/companies-selector/companies-selector-context'
import api from 'src/services/api'
import {LprFilterType} from 'src/services/api/endpoints/LprEndpoint'
import createStyleSheet from 'src/utilities/createStyleSheet'
import handleErrorWithSnackbar from 'src/utilities/handleErrorWithSnackbar'
import CustomCalendar from '../../components/customCalendar/CustomCalendar'
import {ContainerFilters, FiltersSelectedContainer} from '../alarms/AlarmsBase'
import LicensePlateGridTable from './components/lpr-grid-table/LprGridTable'
import {ContainerHeaderLpr, TitleOnButtonResponsive} from './LicensePlateBase'

export type DateFilter = {
  start?: string
  end?: string
}

export type SentToFilter = {
  sentToHarpia?: boolean
  sentToSpia?: boolean
  sentToSivana?: boolean
}

const initialSentToItems = [
  {id: 'harpia', label: 'Polícia Rodoviária do Paraná - Harpia'},
  {id: 'spia', label: 'Polícia Rodoviária Federal - Spia'},
  {id: 'sivana', label: 'Receita Federal - Sivana'},
]

const START_DATE = moment(sub(new Date(), {minutes: 30})).format()
const FINAL_DATE = moment(new Date()).endOf('day').format()

export function LicensePlate() {
  const {selectedCompanies} = useCompaniesSelector()

  const [dateFilter, setDateFilter] = useState<DateFilter>({start: START_DATE, end: FINAL_DATE})
  const {enqueueSnackbar} = useSnackbar()

  const initalSearchTypes = [
    {id: '3', label: 'Placa', searchBy: 'plates'},
    {id: '4', label: 'Cidade', searchBy: 'city'},
    {id: '5', label: 'Cor', searchBy: 'color'},
    {id: '6', label: 'Marca', searchBy: 'brand'},
    {id: '7', label: 'Modelo', searchBy: 'model'},
  ]

  const [searchTypes, setSearchTypes] = useState(initalSearchTypes)

  const [searchSelected, setSearchSelected] = useState<MultipleSearchDropdownItem>({id: '', label: '', searchBy: ''})
  const [showMoreFilters, setShowMoreFilters] = useState(false)
  const [chosenFilters, setChosenFilters] = useState<FilterSelectedFinal[]>([])
  const [inputSearch, setInputSearch] = useState('')

  const [sentTo, setSentTo] = useState<SentToFilter>({sentToHarpia: undefined, sentToSpia: undefined, sentToSivana: undefined})
  const [sentToItems, setSentToItems] = useState<Array<DropdownItem>>(initialSentToItems)

  const [disabledSelectFilters, setDisabledSelectFilters] = useState(false)
  const [filter, setFilter] = useState<LprFilterType>({companyIds: selectedCompanies.map((it) => it.id)})
  const initalCamerasValue = useRef<DropdownItem[]>([])

  const [cameras, setCameras] = useState<Array<DropdownItem>>([])
  const [clients, setClients] = useState<Array<DropdownItem>>([])
  const initalClientsValue = useRef<DropdownItem[]>([])

  const isFilteringPastDate = () => {
    const withoutMiliTo = new Date(dateFilter.end ?? '').setMilliseconds(0)
    const withoutMiliNow = new Date().setMilliseconds(0)
    return dateFilter.end && dateFilter.start && withoutMiliTo < withoutMiliNow
  }
  const [initialDatesToShowOnCalendar, setInitalDatesToShowOnCalendar] = useState<[Date, Date]>([new Date(dateFilter.start!), new Date(dateFilter.end!)])

  useEffect(() => {
    handleConnectOrDisconnectSocketByFilters()
  }, [dateFilter.end, dateFilter.start])

  const handleConnectOrDisconnectSocketByFilters = async () => {
    if (isFilteringPastDate()) {
      forceDisconnect()
    } else if (websocket.webSocketConnectionStatus === WebSocketStatusesEnum.DISCONNECTED && !chosenFilters.length && !inputSearch) {
      await websocket.forceConnect()
    }
  }

  const {
    data: lprEvents,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    websocket,
    forceDisconnect,
  } = useLicensePlate({
    filters: {...filter, companyIds: selectedCompanies.map((it) => it.id)},
    sentTo: sentTo,
    start: dateFilter.start !== '' ? moment(dateFilter.start).format() : START_DATE,
    end: dateFilter.end !== '' ? moment(dateFilter.end).format() : FINAL_DATE,
    enabled: true,
  })

  const [isDownloadButtonDisabled, setIsDownloadButtonDisabled] = useState(false)

  function handleMultipleSearch() {
    forceDisconnect()
    let newFilter = {}
    if (searchSelected.searchBy === 'plates') {
      newFilter = {
        [searchSelected.searchBy]: [inputSearch],
      }
    } else {
      newFilter = {[searchSelected.searchBy]: inputSearch}
    }

    setFilter((prev) => {
      return {
        ...prev,
        ...newFilter,
      }
    })

    const setSearch = searchTypes.filter((it) => it.searchBy !== searchSelected.searchBy)
    setSearchTypes(setSearch)

    const newChosenFilters = {
      id: searchSelected.id,
      label: searchSelected.label,
      itemsOnMenu: [],
      selectedItem: {
        id: '',
        label: inputSearch,
      },
    }
    setChosenFilters((prev) => {
      return [...prev, newChosenFilters]
    })

    setShowMoreFilters(true)
    setInputSearch('')
  }

  const {data: clientsData} = useClients({selectedCompanies: selectedCompanies.map((it) => it.id)})
  const {data: camerasData} = useCameras({selectedCompanies: selectedCompanies.map((it) => it.id), originsToFilter: ['RTSP', 'RTSP-DVR', 'RTMP']})

  useEffect(() => {
    if (!clientsData) return
    const initialClients = clientsData
      ? clientsData.data.data.entities.map((client) => {
          return {id: String(client.id), label: client.name}
        })
      : []
    initalClientsValue.current = initialClients
    setClients(initialClients)
  }, [clientsData])

  useEffect(() => {
    if (!camerasData) return
    const initialCameras = camerasData
      ? camerasData.data.data.entities.map((camera) => {
          return {id: String(camera.id), label: camera.name}
        })
      : []
    setCameras(initialCameras)
    initalCamerasValue.current = initialCameras
  }, [camerasData])

  const handleChangeTimeStart = (dates: [Date, Date]) => {
    let start = moment(dates[0]).format()
    let end = moment(dates[1]).format()
    if (dates[0] === dates[1]) {
      start = moment(dates[0].setHours(0, 0, 0, 0)).format()
      end = moment(dates[1].setHours(23, 59, 59, 59)).format()
    }
    setDateFilter({
      start,
      end,
    })

    setInitalDatesToShowOnCalendar(dates)
  }

  const eventFilters = [
    {
      id: '1',
      label: 'Enviado',
      itemsOnMenu: sentToItems,
    },
    {
      id: '2',
      label: 'Câmeras',
      itemsOnMenu: cameras,
    },
    {
      id: '3',
      label: 'Clientes',
      itemsOnMenu: clients,
    },
  ]

  function handlePartialSetFilter(newFilter: FilterSelectedFinal) {
    if (!sentToItems.length && newFilter.id === '1') return
    if (!cameras.length && newFilter.id === '2') return
    if (!clients.length && newFilter.id === '3') return

    setDisabledSelectFilters(true)
    const moreFilters = [...chosenFilters, newFilter]
    setChosenFilters(moreFilters)
    setShowMoreFilters(true)
  }

  const handleSetPartialFilters = (value: DropdownItem, index: number) => {
    setDisabledSelectFilters(false)
    const newFilter = [...chosenFilters]
    newFilter[index].selectedItem = value

    const newList = sentToItems.filter((it) => it.label !== value.label)
    setSentToItems(newList)

    const newCameras = cameras.filter((it) => it.label !== value.label)
    setCameras(newCameras)

    const newClients = clients.filter((it) => it.label !== value.label)
    setClients(newClients)

    if (newFilter[index].label === 'Enviado') {
      newFilter[index].itemsOnMenu = newList

      if (value.id === 'spia') {
        setSentTo((prev) => {
          return {
            ...prev,
            sentToSpia: true,
          }
        })
      } else if (value.id === 'harpia') {
        setSentTo((prev) => {
          return {
            ...prev,
            sentToHarpia: true,
          }
        })
      } else {
        setSentTo((prev) => {
          return {
            ...prev,
            sentToSivana: true,
          }
        })
      }
    }
    if (newFilter[index].label === 'Câmeras') {
      const selectedId = newFilter[index].selectedItem?.id
      if (!selectedId) return
      const previousCamerasIds = filter.cameraIds ?? []
      const newArrayOfCamerasFilter = [...previousCamerasIds, +selectedId]
      setFilter((prev) => {
        return {
          ...prev,
          cameraIds: newArrayOfCamerasFilter,
        }
      })
      newFilter[index].itemsOnMenu = newCameras
    }
    if (newFilter[index].label === 'Clientes') {
      const selectedId = newFilter[index].selectedItem?.id
      if (!selectedId) return
      const previousClientsIds = filter.clientIds ?? []
      const newArrayOfClientsFilter = [...previousClientsIds, +selectedId]
      setFilter((prev) => {
        return {
          ...prev,
          clientIds: newArrayOfClientsFilter,
        }
      })
      newFilter[index].itemsOnMenu = newClients
    }

    setChosenFilters(newFilter)
    forceDisconnect()
  }

  const deleteFilter = async (index: number) => {
    const deleteItem = chosenFilters[index].selectedItem?.label

    if (!deleteItem) return

    const deleted = {
      id: chosenFilters[index].selectedItem?.id ?? '',
      label: deleteItem,
    }

    if (chosenFilters[index].label === 'Enviado') {
      if (deleteItem === 'Spia') {
        setSentTo((prev) => {
          return {
            ...prev,
            sentToSpia: undefined,
          }
        })
      } else {
        setSentTo((prev) => {
          return {
            ...prev,
            sentToHarpia: undefined,
          }
        })
      }

      setSentToItems((prev) => [...prev, deleted])
    } else if (chosenFilters[index].label === 'Câmeras') {
      const selectedId = chosenFilters[index].selectedItem?.id
      if (!selectedId) return
      const newArrayOfCamerasFilter = filter.cameraIds?.filter((it) => it !== +selectedId) ?? []

      setFilter((prev) => {
        return {
          ...prev,
          cameraIds: newArrayOfCamerasFilter,
        }
      })

      setCameras((prev) => [...prev, deleted])
    } else if (chosenFilters[index].label === 'Clientes') {
      const selectedId = chosenFilters[index].selectedItem?.id
      if (!selectedId) return
      const newArrayOfClientsFilter = filter.clientIds?.filter((it) => it !== +selectedId) ?? []

      setFilter((prev) => {
        return {
          ...prev,
          clientIds: newArrayOfClientsFilter,
        }
      })

      setClients((prev) => [...prev, deleted])
    } else {
      const typeToDelete = initalSearchTypes.find((it) => it.label === chosenFilters[index].label)

      const keyToRemove = typeToDelete?.searchBy
      const newFilter = filter
      delete newFilter[keyToRemove as keyof typeof filter]
      setFilter(newFilter)
      setSearchTypes((prev) => [...prev, typeToDelete!])
      setInputSearch('')
    }

    const deletedFilters = chosenFilters.filter((it) => it.selectedItem?.label !== deleteItem)
    setChosenFilters(deletedFilters)
    if (deletedFilters.length <= 0) {
      setShowMoreFilters(false)
      await handleConnectOrDisconnectSocketByFilters()
    }
  }

  const deleteAllFilters = async () => {
    setSentTo((prev) => {
      return {
        ...prev,
        sentToSpia: undefined,
        sentToHarpia: undefined,
      }
    })
    setSentToItems(initialSentToItems)
    setCameras(initalCamerasValue.current)
    setFilter({companyIds: selectedCompanies.map((it) => it.id)})
    setSearchTypes(initalSearchTypes)
    setDisabledSelectFilters(false)
    setShowMoreFilters(false)
    setChosenFilters([])
    await handleConnectOrDisconnectSocketByFilters()
  }

  const clearEmptyFilter = () => {
    const newFilters = chosenFilters.filter((it) => it.selectedItem)
    setChosenFilters(newFilters)
    setDisabledSelectFilters(false)
  }

  async function handleGetCsv(dateFilter: DateFilter, sentTo: SentToFilter) {
    setIsDownloadButtonDisabled(true)
    try {
      const response = await api.lpr.getCsv({
        filter: {...filter, companyIds: selectedCompanies.map((it) => it.id)},
        sentToHarpia: sentTo.sentToHarpia ?? undefined,
        sentToSpia: sentTo.sentToSpia ?? undefined,
        start: dateFilter.start !== '' ? moment(dateFilter.start).format() : moment(sub(new Date(), {minutes: 30})).format(),
        end: dateFilter.end !== '' ? moment(dateFilter.end).format() : moment(new Date()).format(),
        orderBy: {createdAt: 'DESC'},
        includes: ['camera', 'plate.vehicle', 'camera.configuration'],
      })

      if (response) {
        const url = window.URL.createObjectURL(response)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'placas_detectadas.csv')
        document.body.appendChild(link)
        link.click()
        link.remove()
      } else {
        handleErrorWithSnackbar(enqueueSnackbar, 'Erro ao baixar o arquivo', 'Tente novamente')
      }
    } catch (error) {
      handleErrorWithSnackbar(enqueueSnackbar, error, 'Erro ao baixar o arquivo')
    } finally {
      setIsDownloadButtonDisabled(false)
    }
  }

  const onPressBackToLive = async () => {
    setChosenFilters([])
    setShowMoreFilters(false)
    setInitalDatesToShowOnCalendar([new Date(START_DATE), new Date(FINAL_DATE)])
    setDateFilter({start: START_DATE, end: FINAL_DATE})
    setFilter({companyIds: selectedCompanies.map((it) => it.id)})
    await websocket.forceConnect()
  }

  return (
    <Box style={styles.paper}>
      <TopBar>
        <ContainerHeaderLpr>
          <SelectWithSearchComponent
            searchBy={searchTypes}
            setSearchSelected={setSearchSelected}
            searchSelected={searchSelected}
            inputValue={inputSearch}
            inputSearch={setInputSearch}
            filterBy={eventFilters}
            setSelectedFilter={handlePartialSetFilter}
            disabledSelectFilter={disabledSelectFilters}
            handleMultipleSearch={handleMultipleSearch}
            multipleSearch
          />

          <Box display='flex' align='center' columnGap='5px' justifyContent='center'>
            <WebSocketStatus status={websocket.webSocketConnectionStatus} onPressBackToLive={onPressBackToLive} onPressDisconnect={forceDisconnect} />
            <CustomCalendar initialDates={initialDatesToShowOnCalendar} setDate={(dates) => handleChangeTimeStart(dates)} />

            <Button
              variant='outlined'
              height='40px'
              hoverColor='none'
              isLoading={isDownloadButtonDisabled}
              loadingColor='#258BE3'
              disabled={isDownloadButtonDisabled}
              onClick={() => handleGetCsv(dateFilter, sentTo)}>
              <Box display='flex' columnGap='8px' align='center'>
                <DownloadIcon />
                <TitleOnButtonResponsive>Baixar</TitleOnButtonResponsive>
              </Box>
            </Button>
          </Box>
        </ContainerHeaderLpr>

        <Box marginTop='15px' align='center' display='flex' justifyContent='center'>
          {showMoreFilters && (
            <ContainerFilters>
              <FiltersSelectedContainer>
                {chosenFilters.map((it, index) => {
                  return (
                    <AdvancedFilters
                      key={`${it}_${index}`}
                      clearEmptyFilter={clearEmptyFilter}
                      index={index}
                      filtersSelected={it}
                      setFiltersSelected={handleSetPartialFilters}
                      deleteFilter={deleteFilter}
                    />
                  )
                })}
              </FiltersSelectedContainer>

              <TransparentButton onClick={() => deleteAllFilters()}>
                <Box display='flex' columnGap='10px' align='center'>
                  <DeleteIcon />
                  <p>Limpar Seleção</p>
                </Box>
              </TransparentButton>
            </ContainerFilters>
          )}
        </Box>
      </TopBar>

      <LicensePlateGridTable
        lprEvents={lprEvents}
        loadingInformation={isLoading}
        loadingMore={isFetchingNextPage}
        filter={filter}
        setFilter={(filter) => {
          setFilter(filter)
          forceDisconnect()
        }}
        fetchNextPage={fetchNextPage}
        canLoadMore={hasNextPage}
      />
    </Box>
  )
}

const styles = createStyleSheet({
  paper: {
    borderRadius: '8px',
    flexDirection: 'column',
    background: 'white',
  },
})
